if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          thumbnailSlideButtons: this.querySelector('[id^="GalleryThumbnails"]')?.querySelectorAll('swiper-slide.slider__slide .thumbnail'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        
        if (!this.elements.thumbnails) return;

        this.swiperEl = this.querySelector('swiper-container');
        this.swiperParams = {
          slidesPerView: 4, // If you change this number, update it in liquid too
          freeMode: true,
          watchOverflow: true,
          watchSlidesVisibility: true,
          watchSlidesProgress: true,
          spaceBetween: 3,
          multipleActiveThumbs: false,
          breakpoints: {
            750: {
              direction: 'vertical',
              spaceBetween: 10,
              slidesPerView: 7,
            },
          },
          on: {
            resize: () => {
              this.swiperNavResizeToggle();
            },
            init: () => {
              this.swiperNavResizeToggle();
            },
            slideChange: () => {
              this.toggleSwiperNavigationButtonsVisibility();
            },
            slideChangeTransitionEnd: () => {
              this.toggleSwiperNavigationButtonsVisibility();
            },
          }
        };
        this.swiperElNextBtn = this.querySelector('.swiper-button--next');
        this.swiperElPrevBtn = this.querySelector('.swiper-button--prev');

        this.initSwiperSliderEl();

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      initSwiperSliderEl() {
        
        if (!this.swiperEl) {
          return
        }

        Object.assign(this.swiperEl, this.swiperParams);
        this.swiperEl.initialize();
    
        this.swiperElNextBtn.addEventListener('click', this.handleSwiperElNextBtn.bind(this));
        this.swiperElPrevBtn.addEventListener('click', this.handleSwiperElPrevBtn.bind(this));
      }

      triggerThumbnailClick(index) {
        const matchingBtn = this.elements.thumbnailSlideButtons[index];

        if (!matchingBtn) {
          return;
        }

        matchingBtn.click();
        
        if (this.swiperEl) {
          this.swiperEl.swiper.slideTo(index);
        }
      }

      swiperNavResizeToggle() {
        if (!this.swiperEl) {
          return;
        }

        if (this.swiperEl.swiper?.visibleSlides) {
          if (this.swiperEl.swiper?.visibleSlides.length < this.swiperEl.swiper?.slides.length) {
            this.toggleSwiperNavigationButtonsVisibility();
          } else {
            this.swiperElNextBtn.classList.add('invisible');
            this.swiperElPrevBtn.classList.add('invisible');
            this.swiperElNextBtn.setAttribute('disabled', true);
            this.swiperElPrevBtn.setAttribute('disabled', true);
          }
        }
      }

      toggleSwiperNavigationButtonsVisibility() {
        if (!this.swiperEl) {
          return;
        }

        if (this.swiperEl.swiper.isEnd) {
          this.swiperElNextBtn.setAttribute('disabled', true);
          this.swiperElNextBtn.classList.add('invisible');
          this.swiperElPrevBtn.classList.remove('invisible');
          this.swiperElPrevBtn.removeAttribute('disabled');
          
          return
        }
        
        if (this.swiperEl.swiper.isBeginning) {
          this.swiperElPrevBtn.setAttribute('disabled', true);
          this.swiperElPrevBtn.classList.add('invisible');
          this.swiperElNextBtn.classList.remove('invisible');
          this.swiperElNextBtn.removeAttribute('disabled');

          return
        }

        this.swiperElNextBtn.classList.remove('invisible');
        this.swiperElNextBtn.removeAttribute('disabled');
        this.swiperElPrevBtn.classList.remove('invisible');
        this.swiperElPrevBtn.removeAttribute('disabled');
      }

      handleSwiperElNextBtn() {
        this.swiperEl.swiper.slideNext();
        this.triggerThumbnailClick(this.swiperEl.swiper.activeIndex);
        this.toggleSwiperNavigationButtonsVisibility();
      }
    
      handleSwiperElPrevBtn() {
        this.swiperEl.swiper.slidePrev();
        this.triggerThumbnailClick(this.swiperEl.swiper.activeIndex);
        this.toggleSwiperNavigationButtonsVisibility();
      }

      onSlideChanged(event) {
        if (event.detail.currentElement) {
          const thumbnail = this.elements.thumbnails.querySelector(
            `[data-target="${event.detail.currentElement.dataset.mediaId}"]`,
          );
          this.setActiveThumbnail(thumbnail);
        }        
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }

          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
